import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAPI, postAPI, putAPI, deleteAPI } from '../../utils/utils';
import './FamiliesTab.css';
import { getCastellerNameWithMote } from '../../utils/castellerName';

function FamilyCard({ group, castellersInfo, castellersInFamilies, onUpdate, onDelete }) {
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(group.nom || '');
    const [members, setMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);

    useEffect(() => {
        fetchAPI(`/family-groups/${group.id}`, data => {
            setMembers(data.members?.map(m => ({
                id: m.casteller_id,
                memberId: m.id,
                nom: m.nom,
                cognom: m.cognom,
                mote: m.mote,
                isChild: m.is_child
            })) || []);
            setLoadingMembers(false);
        }, false);
    }, [group.id]);

    const searchCastellers = (term) => {
        if (term.length < 2) return [];
        const termLower = term.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const currentMemberIds = members.map(m => m.id);
        // Exclude castellers already in OTHER families (not current family members)
        const othersInFamilies = (castellersInFamilies || []).filter(id => !currentMemberIds.includes(id));
        const excludeIds = [...currentMemberIds, ...othersInFamilies];
        return Object.values(castellersInfo || {})
            .filter(c => !excludeIds.includes(c.id))
            .filter(c => {
                const fullName = `${c.nom || ''} ${c['primer-cognom'] || ''} ${c.mote || ''}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                return fullName.includes(termLower);
            })
            .slice(0, 8);
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setSearchResults(searchCastellers(term));
    };

    const handleAddMember = (casteller) => {
        postAPI(`/family-groups/${group.id}/members`, {
            castellerId: casteller.id,
            isChild: false
        }, (result) => {
            setMembers(prev => [...prev, {
                id: casteller.id,
                memberId: result.id,
                nom: casteller.nom,
                cognom: casteller['primer-cognom'],
                mote: casteller.mote,
                isChild: false
            }]);
            onUpdate();
        }, (err) => {
            alert(err?.message || 'Error afegint membre');
        });
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveMember = (member) => {
        deleteAPI(`/family-groups/${group.id}/members/${member.memberId}`, () => {
            setMembers(prev => prev.filter(m => m.id !== member.id));
            onUpdate();
        }, () => {
            alert('Error eliminant membre');
        });
    };

    const handleToggleChild = (member) => {
        putAPI(`/family-groups/${group.id}/members/${member.memberId}`, {
            isChild: !member.isChild
        }, () => {
            setMembers(prev => prev.map(m =>
                m.id === member.id ? { ...m, isChild: !m.isChild } : m
            ));
            onUpdate();
        }, () => {
            alert('Error actualitzant membre');
        });
    };

    const handleNameBlur = () => {
        if (name !== (group.nom || '')) {
            putAPI(`/family-groups/${group.id}`, { nom: name }, onUpdate, () => {
                alert('Error desant el nom');
            });
        }
    };

    const handleDelete = () => {
        if (!window.confirm('Segur que vols eliminar aquest grup familiar?')) return;
        onDelete(group.id);
    };

    const handleDoneEditing = () => {
        setIsEditing(false);
        setSearchTerm('');
        setSearchResults([]);
    };

    return (
        <div className={`family-card ${isEditing ? 'editing' : ''}`}>
            <div className="card-header">
                {isEditing ? (
                    <input
                        type="text"
                        className="family-name-input"
                        placeholder="Nom de la família"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onBlur={handleNameBlur}
                    />
                ) : (
                    <span className="family-name-display">
                        {name || `Família #${group.id}`}
                    </span>
                )}
                <div className="card-actions">
                    {isEditing ? (
                        <>
                            <button className="delete-btn" onClick={handleDelete} title="Eliminar">
                                🗑️
                            </button>
                            <button className="done-btn" onClick={handleDoneEditing}>
                                ✓ Fet
                            </button>
                        </>
                    ) : (
                        <button className="edit-btn" onClick={() => setIsEditing(true)}>
                            Editar
                        </button>
                    )}
                </div>
            </div>

            {/* Search to add members - only in edit mode */}
            {isEditing ? (
                <div className="member-search">
                    <input
                        type="text"
                        placeholder="Afegir membre..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    {searchResults.length > 0 ? (
                        <div className="search-dropdown">
                            {searchResults.map(c => (
                                <div
                                    key={c.id}
                                    className="search-item"
                                    onClick={() => handleAddMember(c)}
                                >
                                    {getCastellerNameWithMote(c)}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Members list */}
            {loadingMembers ? (
                <div className="loading-members">Carregant...</div>
            ) : members.length > 0 ? (
                <div className="members-list">
                    {members.map(m => (
                        <div key={m.id} className="member-row">
                            <span className="member-name">
                                {getCastellerNameWithMote(m)}
                            </span>
                            {isEditing ? (
                                <>
                                    <label className="child-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={m.isChild}
                                            onChange={() => handleToggleChild(m)}
                                        />
                                        Infant
                                    </label>
                                    <button
                                        className="remove-btn"
                                        onClick={() => handleRemoveMember(m)}
                                    >
                                        ×
                                    </button>
                                </>
                            ) : m.isChild ? (
                                <span className="infant-badge">Infant</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="no-members">Sense membres</p>
            )}
        </div>
    );
}

function FamiliesTab({ socket, castellersInfo }) {
    const [familyGroups, setFamilyGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [filterTerm, setFilterTerm] = useState('');
    const [castellersInFamilies, setCastellersInFamilies] = useState([]);

    // New family creation state
    const [isCreating, setIsCreating] = useState(false);
    const [newFamilyName, setNewFamilyName] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const searchRef = useRef(null);

    const fetchCastellersInFamilies = useCallback(() => {
        fetchAPI('/family-groups/members/all-ids', data => {
            setCastellersInFamilies(data || []);
        }, false);
    }, []);

    const fetchFamilyGroups = useCallback((showLoading = true) => {
        if (showLoading) setLoading(true);
        fetchAPI('/family-groups', data => {
            setFamilyGroups(data || []);
            if (showLoading) setLoading(false);
        }, false);
        fetchCastellersInFamilies();
    }, [fetchCastellersInFamilies]);

    const silentRefresh = useCallback(() => fetchFamilyGroups(false), [fetchFamilyGroups]);

    useEffect(() => {
        fetchFamilyGroups();

        if (socket) {
            socket.on('.family_groups_updated', silentRefresh);
            return () => socket.off('.family_groups_updated', silentRefresh);
        }
    }, [fetchFamilyGroups, silentRefresh, socket]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setSearchResults([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchCastellers = (term, excludeIds = []) => {
        if (term.length < 2) return [];
        const termLower = term.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const allExcluded = [...excludeIds, ...castellersInFamilies];
        return Object.values(castellersInfo || {})
            .filter(c => !allExcluded.includes(c.id))
            .filter(c => {
                const fullName = `${c.nom || ''} ${c['primer-cognom'] || ''} ${c.mote || ''}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                return fullName.includes(termLower);
            })
            .slice(0, 8);
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const excludeIds = selectedMembers.map(m => m.id);
        setSearchResults(searchCastellers(term, excludeIds));
    };

    const handleAddToSelection = (casteller) => {
        setSelectedMembers(prev => [...prev, {
            id: casteller.id,
            nom: casteller.nom,
            cognom: casteller['primer-cognom'],
            mote: casteller.mote,
            isChild: false
        }]);
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveFromSelection = (id) => {
        setSelectedMembers(prev => prev.filter(m => m.id !== id));
    };

    const handleToggleChild = (id) => {
        setSelectedMembers(prev => prev.map(m =>
            m.id === id ? { ...m, isChild: !m.isChild } : m
        ));
    };

    const handleCreateFamily = async () => {
        if (selectedMembers.length === 0) {
            alert('Has de seleccionar almenys un membre');
            return;
        }
        setSaving(true);

        postAPI('/family-groups', { nom: newFamilyName || null }, async (result) => {
            if (result.id) {
                const addedMembers = [];
                const errors = [];

                for (const member of selectedMembers) {
                    await new Promise((resolve) => {
                        postAPI(`/family-groups/${result.id}/members`, {
                            castellerId: member.id,
                            isChild: member.isChild
                        },
                        (memberResult) => {
                            addedMembers.push({ ...member, memberId: memberResult.id });
                            resolve();
                        },
                        (error) => {
                            errors.push({ member, error });
                            resolve();
                        });
                    });
                }

                if (errors.length > 0) {
                    alert(`Alguns membres no s'han pogut afegir:\n${errors.map(e =>
                        `${getCastellerNameWithMote(e.member)}: ${e.error?.message || 'Error desconegut'}`
                    ).join('\n')}`);
                }
            }
            setSaving(false);
            setIsCreating(false);
            setNewFamilyName('');
            setSelectedMembers([]);
        }, (error) => {
            setSaving(false);
            alert('Error creant el grup familiar: ' + (error?.message || 'Error desconegut'));
        });
    };

    const handleCancelCreate = () => {
        setIsCreating(false);
        setNewFamilyName('');
        setSelectedMembers([]);
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleDeleteGroup = (groupId) => {
        deleteAPI(`/family-groups/${groupId}`, () => fetchFamilyGroups(false), () => {
            alert('Error eliminant el grup');
        });
    };

    if (loading) {
        return (
            <div className="families-tab">
                <div className="families-loading">Carregant...</div>
            </div>
        );
    }

    return (
        <div className="families-tab">
            <div className="families-header">
                <div>
                    <h2>Grups Familiars</h2>
                    <p className="families-description">
                        Gestiona els grups familiars per permetre pagaments conjunts de quotes.
                    </p>
                </div>
                {familyGroups.length > 0 && (
                    <input
                        type="text"
                        className="families-filter"
                        placeholder="Filtrar famílies..."
                        value={filterTerm}
                        onChange={e => setFilterTerm(e.target.value)}
                    />
                )}
            </div>

            {/* Inline Create Form */}
            {isCreating ? (
                <div className="create-form">
                    <div className="create-form-header">
                        <input
                            type="text"
                            className="family-name-input"
                            placeholder="Nom de la família (opcional)"
                            value={newFamilyName}
                            onChange={e => setNewFamilyName(e.target.value)}
                        />
                    </div>

                    <div className="member-search" ref={searchRef}>
                        <input
                            type="text"
                            placeholder="Cerca membres..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchResults.length > 0 ? (
                            <div className="search-dropdown">
                                {searchResults.map(c => (
                                    <div
                                        key={c.id}
                                        className="search-item"
                                        onClick={() => handleAddToSelection(c)}
                                    >
                                        {getCastellerNameWithMote(c)}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {selectedMembers.length > 0 ? (
                        <div className="members-list">
                            {selectedMembers.map(m => (
                                <div key={m.id} className="member-row">
                                    <span className="member-name">
                                        {getCastellerNameWithMote(m)}
                                    </span>
                                    <label className="child-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={m.isChild}
                                            onChange={() => handleToggleChild(m.id)}
                                        />
                                        Infant
                                    </label>
                                    <button
                                        className="remove-btn"
                                        onClick={() => handleRemoveFromSelection(m.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="hint">Cerca i selecciona els membres de la família</p>
                    )}

                    <div className="create-form-actions">
                        <button className="cancel-btn" onClick={handleCancelCreate}>
                            Cancel·lar
                        </button>
                        <button
                            className="save-btn"
                            onClick={handleCreateFamily}
                            disabled={saving || selectedMembers.length === 0}
                        >
                            {saving ? 'Creant...' : 'Crear família'}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    className="new-family-btn"
                    onClick={() => setIsCreating(true)}
                >
                    + Nova família
                </button>
            )}

            {/* Existing families - always expanded */}
            <div className="families-list">
                {familyGroups.length === 0 && !isCreating ? (
                    <p className="empty-state">No hi ha grups familiars creats</p>
                ) : null}

                {familyGroups
                    .filter(group => {
                        if (!filterTerm) return true;
                        const term = filterTerm.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                        const nom = (group.nom || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                        const memberNames = (group.member_names || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                        return nom.includes(term) || memberNames.includes(term);
                    })
                    .map(group => (
                        <FamilyCard
                            key={group.id}
                            group={group}
                            castellersInfo={castellersInfo}
                            castellersInFamilies={castellersInFamilies}
                            onUpdate={() => fetchFamilyGroups(false)}
                            onDelete={handleDeleteGroup}
                        />
                    ))}
            </div>
        </div>
    );
}

export default FamiliesTab;
